import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Tab, Tabs } from '@mui/material';
import CompanySelect from 'components/company/CompanySelect';
import { ConfigContext } from 'components/ConfigGuard';
import { ParametersContext } from 'components/ParametersGuard';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import useRequest from 'hooks/useRequest';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import ApplicationsService from 'services/api/ApplicationsService';
import ClaimsService from 'services/api/ClaimsService';
import routes from 'store/configs/Routes';
import ClaimStatus from 'store/enums/ClaimStatus';
import ClaimType from 'store/enums/ClaimType';
import ClaimUploadType from 'store/enums/ClaimUploadType';
import SiteModule from 'store/enums/SiteModule';
import Application from 'store/types/Application';
import ApplicationType from 'store/types/ApplicationType';
import { Claim } from 'store/types/Claim';
import { TabItem } from 'store/types/Tabs';
import { CompanySelectOption, getCompanySelectOptions } from 'util/Company';
import { defaultSnackbarErrorProps } from 'util/Layout';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';
import ActiveClaimsTab from './ActiveClaimsTab';
import CloseClaimModal from './CloseClaimModal';
import DeleteDraftApplicationModal from './DeleteDraftApplicationModal';
import DisputeClaimModal from './DisputeClaimModal';
import HistoricClaimsTab from './HistoricClaimsTab';
import ReviewClaimsTab from './ReviewClaimsTab';
import UploadClaimFilesModal from './UploadClaimFilesModal';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  ActiveClaimsAndDisputes: {
    key: 'ActiveClaimsAndDisputes',
    label: 'Active',
    route: routes.claimsAndDisputes,
  } as TabItem,
  HistoricClaimsAndDisputes: {
    key: 'HistoricClaimsAndDisputes',
    label: 'Historic',
    route: routes.claimsAndDisputesHistoric,
  } as TabItem,
  JudgesReviewClaimsAndDisputes: {
    key: 'JudgesReviewClaimsAndDisputes',
    label: 'Judges Review',
    route: routes.claimsAndDisputesJudgeReview,
  } as TabItem,
};

const isClaimClosed = (c: Claim) =>
  (c.type === ClaimType.Claim && c.claimStatus === ClaimStatus.Closed) ||
  (c.type === ClaimType.Dispute && c.disputeStatus === ClaimStatus.Closed);

const ClaimsPage: React.FunctionComponent = () => {
  const { replace, push } = useHistory();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { functions = [], userLoading, userPermissions } = useContext(UserContext);
  const {
    modulesSettings: {
      claims: { hiddenTabs },
    },
  } = useContext(ConfigContext);

  const [selectedCompany, setSelectedCompany] = useState<CompanySelectOption | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim>();
  const [uploadType, setUploadType] = useState<ClaimUploadType>(ClaimUploadType.Files);

  const {
    claimsAndDisputes: { claimsAndDisputesPageHeader, historicClaimsAndDisputesPageHeader, underReviewPageHeader },
  } = useContext(ParametersContext);
  const [submitLoading, setSubmitLoading] = useState<boolean>();

  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.ActiveClaimsAndDisputes.key) => {
      replace(getTabRouteByKey(value, tabConfig));
    },
    [replace]
  );

  const permissions: string[] = useMemo(
    () => userPermissions.modules.find(({ name }) => name === SiteModule.Company)?.permissions || [],
    [userPermissions]
  );
  const options: CompanySelectOption[] = useMemo(
    () => getCompanySelectOptions(functions, permissions),
    [functions, permissions]
  );

  const claimsRequest = useMemo(
    () => (selectedCompany?.id ? () => ClaimsService.getClaims(selectedCompany.id) : undefined),
    [selectedCompany]
  );
  const { data = [], loading, refetch } = useRequest<Claim[]>(claimsRequest);

  const applicationTypesRequest = useMemo(
    () => (selectedCompany?.id ? () => ApplicationsService.getApplicationTypes(selectedCompany.id) : undefined),
    [selectedCompany?.id]
  );

  const { data: applicationTypes, loading: applicationTypesLoading } =
    useRequest<ApplicationType[]>(applicationTypesRequest);

  const myClaims = useMemo(
    () => data?.filter((c) => c.debtorCompanyId !== selectedCompany?.id && !isClaimClosed(c)),
    [data, selectedCompany?.id]
  );
  const claimsAgainstMe = useMemo(
    () => data?.filter((c) => c.debtorCompanyId === selectedCompany?.id && !isClaimClosed(c)),
    [data, selectedCompany?.id]
  );
  const historicClaims = useMemo(() => data?.filter((c) => isClaimClosed(c)), [data]);
  const underReviewClaims = useMemo(() => data?.filter((c) => c.type === ClaimType.Dispute && c.isJudge), [data]);

  const handleCompanyChange = useCallback(
    (newCompanyId: string) => {
      setSelectedCompany(options.find(({ id }) => id === newCompanyId) || undefined);
    },
    [options]
  );

  const handleCreateApplication = useCallback(
    (applicationId: string) => {
      if (!applicationId) return;

      setSubmitLoading(true);
      ApplicationsService.createApplication(applicationId, undefined, selectedCompany?.id)
        .then(({ id }: Application) => {
          setSubmitLoading(false);
          enqueueSnackbar('Draft application successfully created', { variant: 'success' });
          push(`${routes.editApplication}/${id}`);
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [enqueueSnackbar, push, selectedCompany?.id]
  );

  const handleDeleteModalOpen = useCallback((claim: Claim) => {
    setSelectedClaim(claim);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteModalSubmit = useCallback(() => {
    setSelectedClaim(undefined);
    refetch();
    setDeleteModalOpen(false);
  }, [refetch]);

  const handleDisputeModalOpen = useCallback((claim: Claim) => {
    setSelectedClaim(claim);
    setDisputeModalOpen(true);
  }, []);

  const handleDisputeModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setDisputeModalOpen(false);
  }, []);

  const handleDisputeModalSubmit = useCallback(() => {
    setSelectedClaim(undefined);
    refetch();
    setDisputeModalOpen(false);
  }, [refetch]);

  const handleCloseModalOpen = useCallback((claim: Claim) => {
    setSelectedClaim(claim);
    setCloseModalOpen(true);
  }, []);

  const handleCloseModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setCloseModalOpen(false);
  }, []);

  const handleCloseModalSubmit = useCallback(() => {
    setSelectedClaim(undefined);
    refetch();
    setCloseModalOpen(false);
  }, [refetch]);

  const handleFileUploadModalOpen = useCallback((claim: Claim, uploadType: ClaimUploadType) => {
    setSelectedClaim(claim);
    setUploadType(uploadType);
    setFileUploadModalOpen(true);
  }, []);

  const handleFileUploadModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setUploadType(ClaimUploadType.Files);
    setFileUploadModalOpen(false);
  }, []);

  useEffect(() => {
    replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [replace, visibleTabs]);

  return (
    <Spinner loading={userLoading || applicationTypesLoading || submitLoading} fullPage={true}>
      <h1 className={commonStyles.pageTitle}>My Claims & Disputes</h1>
      <Box sx={{ mb: 4 }}>
        {claimsAndDisputesPageHeader && activeTab === tabConfig.ActiveClaimsAndDisputes.key && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: claimsAndDisputesPageHeader }} />
        )}
        {historicClaimsAndDisputesPageHeader && activeTab === tabConfig.HistoricClaimsAndDisputes.key && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: historicClaimsAndDisputesPageHeader }} />
        )}
        {underReviewPageHeader && activeTab === tabConfig.JudgesReviewClaimsAndDisputes.key && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: underReviewPageHeader }} />
        )}

        <CompanySelect
          value={selectedCompany?.id}
          options={options}
          disabled={loading}
          onChange={handleCompanyChange}
          size={'small'}
        />
      </Box>
      <TabContext value={activeTab}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor={'primary'}
          textColor={'primary'}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              className={commonStyles.tab}
              classes={{ selected: commonStyles.activeTab }}
            />
          ))}
        </Tabs>
        <TabPanel value={tabConfig.ActiveClaimsAndDisputes.key} className={commonStyles.tabPanel}>
          <ActiveClaimsTab
            myClaims={myClaims}
            claimsAgainstMe={claimsAgainstMe}
            applicationTypes={applicationTypes}
            loading={loading}
            onApplicationCreateClick={handleCreateApplication}
            onApplicationDeleteClick={handleDeleteModalOpen}
            onDisputeClaimClick={handleDisputeModalOpen}
            onCloseClaimClick={handleCloseModalOpen}
            onFileUploadClick={handleFileUploadModalOpen}
          />
        </TabPanel>
        <TabPanel value={tabConfig.HistoricClaimsAndDisputes.key} className={commonStyles.tabPanel}>
          <HistoricClaimsTab historicClaims={historicClaims} companyId={selectedCompany?.id} loading={loading} />
        </TabPanel>
        <TabPanel value={tabConfig.JudgesReviewClaimsAndDisputes.key} className={commonStyles.tabPanel}>
          <ReviewClaimsTab underReviewClaims={underReviewClaims} companyId={selectedCompany?.id} loading={loading} />
        </TabPanel>
      </TabContext>
      {selectedCompany && (
        <>
          <DeleteDraftApplicationModal
            data={selectedClaim}
            open={deleteModalOpen}
            onClose={handleDeleteModalClose}
            onSubmit={handleDeleteModalSubmit}
          />
          <UploadClaimFilesModal
            companyId={selectedCompany?.id}
            data={selectedClaim}
            uploadType={uploadType}
            open={fileUploadModalOpen}
            onClose={handleFileUploadModalClose}
            onSubmit={handleFileUploadModalClose}
          />
          <DisputeClaimModal
            companyId={selectedCompany?.id}
            data={selectedClaim}
            open={disputeModalOpen}
            onClose={handleDisputeModalClose}
            onSubmit={handleDisputeModalSubmit}
          />
          <CloseClaimModal
            companyId={selectedCompany?.id}
            data={selectedClaim}
            open={closeModalOpen}
            onClose={handleCloseModalClose}
            onSubmit={handleCloseModalSubmit}
          />
        </>
      )}
    </Spinner>
  );
};

export default ClaimsPage;
